import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Building2, Settings, DollarSign, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function CompanySettings() {
  const [employer, setEmployer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    loadEmployer();
  }, []);

  const loadEmployer = async () => {
    try {
      const user = await api.auth.me();
      if (user.employer_id) {
        const employers = await api.entities.Employer.filter({ id: user.employer_id });
        if (employers.length > 0) {
          setEmployer(employers[0]);
          setFormData(employers[0]);
        }
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load company settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.entities.Employer.update(employer.id, formData);
      toast.success('Company settings updated successfully');
      loadEmployer();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!employer) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-500">No company found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Company Settings</h1>
          <p className="text-slate-500 mt-1">Manage your company's configuration</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="self-start sm:self-auto bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <Tabs defaultValue="basic" className="space-y-6">
        <TabsList>
          <TabsTrigger value="basic">
            <Building2 className="w-4 h-4 mr-2" />
            Basic Info
          </TabsTrigger>
          <TabsTrigger value="policies">
            <Settings className="w-4 h-4 mr-2" />
            Policies
          </TabsTrigger>
          <TabsTrigger value="rules">
            <DollarSign className="w-4 h-4 mr-2" />
            Rules & Limits
          </TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Company Information</CardTitle>
              <CardDescription>Basic details about your company</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2 space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    value={formData.company_name || ''}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Industry</Label>
                  <Input
                    value={formData.industry || ''}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone || ''}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>

                <div className="md:col-span-2 space-y-2">
                  <Label>HR Contact Email</Label>
                  <Input
                    type="email"
                    value={formData.hr_contact_email || ''}
                    onChange={(e) => setFormData({ ...formData, hr_contact_email: e.target.value })}
                  />
                </div>

                <div className="md:col-span-2 space-y-2">
                  <Label>Address</Label>
                  <Input
                    value={formData.address || ''}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={formData.city || ''}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>State</Label>
                  <Input
                    value={formData.state || ''}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>ZIP Code</Label>
                  <Input
                    value={formData.zip_code || ''}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="policies" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Enrollment Policies</CardTitle>
              <CardDescription>Configure enrollment rules for your company</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <Checkbox
                  checked={formData.allow_qle_enrollment}
                  onCheckedChange={(checked) => setFormData({ ...formData, allow_qle_enrollment: checked })}
                  id="allow_qle"
                />
                <div className="flex-1">
                  <Label htmlFor="allow_qle" className="font-semibold cursor-pointer">
                    Allow Qualifying Life Event Enrollments
                  </Label>
                  <p className="text-sm text-slate-600 mt-1">
                    Employees can enroll mid-year if they experience marriage, birth, divorce, etc.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <Checkbox
                  checked={formData.enforce_dependent_verification}
                  onCheckedChange={(checked) => setFormData({ ...formData, enforce_dependent_verification: checked })}
                  id="enforce_dependent"
                />
                <div className="flex-1">
                  <Label htmlFor="enforce_dependent" className="font-semibold cursor-pointer">
                    Require Dependent Verification Documents
                  </Label>
                  <p className="text-sm text-slate-600 mt-1">
                    Employees must upload birth certificates, marriage licenses, etc. for all dependents
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <Checkbox
                  checked={formData.require_evidence_of_insurability_for_late_enrollment}
                  onCheckedChange={(checked) => setFormData({ ...formData, require_evidence_of_insurability_for_late_enrollment: checked })}
                  id="require_eoi"
                />
                <div className="flex-1">
                  <Label htmlFor="require_eoi" className="font-semibold cursor-pointer">
                    Require Evidence of Insurability for Late Enrollments
                  </Label>
                  <p className="text-sm text-slate-600 mt-1">
                    Late enrollments need medical underwriting approval before coverage activates
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mt-6">
                <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
                  <Label>Employer Wait Period (Days)</Label>
                  <Input
                    type="number"
                    value={formData.employer_wait_period_days || 60}
                    onChange={(e) => setFormData({ ...formData, employer_wait_period_days: parseInt(e.target.value) || 0 })}
                  />
                  <p className="text-sm text-slate-600">
                    Days new hires must wait before becoming eligible for benefits
                  </p>
                </div>

                <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
                  <Label>Default Enrollment Window (Days)</Label>
                  <Input
                    type="number"
                    value={formData.default_enrollment_window_days || 30}
                    onChange={(e) => setFormData({ ...formData, default_enrollment_window_days: parseInt(e.target.value) || 0 })}
                  />
                  <p className="text-sm text-slate-600">
                    Days new hires have to complete enrollment
                  </p>
                </div>

                <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
                  <Label>QLE Enrollment Window (Days)</Label>
                  <Input
                    type="number"
                    value={formData.qle_enrollment_window_days || 60}
                    onChange={(e) => setFormData({ ...formData, qle_enrollment_window_days: parseInt(e.target.value) || 0 })}
                  />
                  <p className="text-sm text-slate-600">
                    Days to enroll after qualifying life event
                  </p>
                </div>

                <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
                  <Label>Late Enrollment Grace Period (Days)</Label>
                  <Input
                    type="number"
                    value={formData.late_enrollment_grace_days || 30}
                    onChange={(e) => setFormData({ ...formData, late_enrollment_grace_days: parseInt(e.target.value) || 0 })}
                  />
                  <p className="text-sm text-slate-600">
                    Grace period for late enrollments
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rules" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Financial Rules & Limits</CardTitle>
              <CardDescription>Configure fees and premium settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
                  <Label>Default Admin Fee (Monthly)</Label>
                  <div className="flex gap-2">
                    <span className="flex items-center px-3 border rounded-l bg-white">$</span>
                    <Input
                      type="number"
                      value={formData.default_admin_fee || 60}
                      onChange={(e) => setFormData({ ...formData, default_admin_fee: parseFloat(e.target.value) || 0 })}
                      className="rounded-l-none"
                    />
                  </div>
                </div>

                <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
                  <Label>Premium Claimable Percentage</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={formData.flex_claimable_percentage || 0.86}
                      onChange={(e) => setFormData({ ...formData, flex_claimable_percentage: parseFloat(e.target.value) || 0 })}
                    />
                    <span className="flex items-center px-3 border rounded-r bg-white">x 100%</span>
                  </div>
                  <p className="text-sm text-slate-600">86% rule for wellness claims</p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
                  <Label>Premium Minimum</Label>
                  <div className="flex gap-2">
                    <span className="flex items-center px-3 border rounded-l bg-white">$</span>
                    <Input
                      type="number"
                      value={formData.flex_amount_min || 600}
                      onChange={(e) => setFormData({ ...formData, flex_amount_min: parseFloat(e.target.value) || 0 })}
                      className="rounded-l-none"
                    />
                  </div>
                </div>

                <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
                  <Label>Premium Maximum</Label>
                  <div className="flex gap-2">
                    <span className="flex items-center px-3 border rounded-l bg-white">$</span>
                    <Input
                      type="number"
                      value={formData.flex_amount_max || 2500}
                      onChange={(e) => setFormData({ ...formData, flex_amount_max: parseFloat(e.target.value) || 0 })}
                      className="rounded-l-none"
                    />
                  </div>
                </div>

                <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
                  <Label>Calculation Increment</Label>
                  <div className="flex gap-2">
                    <span className="flex items-center px-3 border rounded-l bg-white">$</span>
                    <Input
                      type="number"
                      value={formData.flex_calculation_increment || 50}
                      onChange={(e) => setFormData({ ...formData, flex_calculation_increment: parseFloat(e.target.value) || 0 })}
                      className="rounded-l-none"
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
                  <Label>EOI Coverage Threshold</Label>
                  <div className="flex gap-2">
                    <span className="flex items-center px-3 border rounded-l bg-white">$</span>
                    <Input
                      type="number"
                      value={formData.eoi_coverage_threshold || 50000}
                      onChange={(e) => setFormData({ ...formData, eoi_coverage_threshold: parseFloat(e.target.value) || 0 })}
                      className="rounded-l-none"
                    />
                  </div>
                  <p className="text-sm text-slate-600">
                    Coverage amount requiring evidence of insurability
                  </p>
                </div>

                <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
                  <Label>Document Retention (Years)</Label>
                  <Input
                    type="number"
                    value={formData.document_retention_years || 7}
                    onChange={(e) => setFormData({ ...formData, document_retention_years: parseInt(e.target.value) || 0 })}
                  />
                  <p className="text-sm text-slate-600">
                    Years to retain documents
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}